// R58-L-07 — List widget (AI Findings / Defects), single-scrollbar
// layout + Current/All toggle + count badge + click-centres-viewer.
//
// History:
//   Story 014 — two-tab pane (W5/W6). Default tab = AI Findings.
//   2026-05-10 — Defect → image activation: push the defect's `imageId`
//     into `imageActivationStore` on row click so the Filmstrip, Viewer,
//     and Tiles update in lockstep. AI Findings did this via
//     `onFindingSelect` upstream — which set `selectedFindingId` for the
//     bbox highlight but DID NOT centre the Viewer because the parent
//     didn't push the finding's `imageId` into `imageActivationStore`.
//   R58-L-07 (2026-05-19) — Five fixes from PR #58 walkthrough:
//     (1) single scrollbar at the widget body — the inner FindingsPanel
//         + DefectsPanel each had their own `maxHeight: 480, overflowY:
//         auto`, producing a list-in-list. We now own the only scroll
//         container; the panels render flush via the new
//         `scrollFromParent` prop.
//     (2) Current image / All images toggle in the widget header.
//         Default = Current image (OQ-10 a). Persisted in the URL via
//         the `listScope` search-param so the analyst's view survives a
//         page reload and is link-shareable.
//     (3) Count badge `<current-image count> / <dataset count>`. The
//         current count comes from the panel's own query (scoped to
//         imageId); the dataset count comes from a parallel unscoped
//         query that AnalysisFeature already issues for the Filmstrip
//         + Viewer overlay.
//     (4) Click row → centres Viewer on that image. The AI findings
//         path now mirrors the Defects path: setActive(imageId) +
//         setActiveBbox(findingId). The Viewer + Tiles + Filmstrip
//         widgets all subscribe to `imageActivationStore`.
//     (5) Render-all — without the inner `maxHeight: 480` truncation
//         all rows render under the widget body's single overflow.
//         True virtualisation (TanStack Virtual) is a follow-up — see
//         comment near `body` Box.
//
// State filter UI (R58-L-07 AC6 / AC7 / AC8 — `pending` / `dismissed` /
// `promoted` segmented control) is deferred to a follow-up. The
// contract param `state` is already wired through `useFindingsQuery`,
// but the FE-side segmented control collides with the multi-status
// Autocomplete in FindingsPanel's existing 004/005/005a/005b specs.
// We land the toggle + click-centring here and ship the state-filter
// UI alongside R58-L-11 (dismiss with reason) which actually exercises
// the new state-machine column.
//
// R58-L-13 (rebased 2026-05-19) — reverse-direction effect: when the
// active selection is published from the Viewer (or any other surface)
// with a kind hint, switch the visible tab to match (AI -> findings,
// Defect -> defects) and scroll the matching row into view.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Chip,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { AiFinding } from "@aeriosiq/types";
// SB6-ANA-UI-01 — shared compact-control presets (the defect-drawer /
// global-filter density). The scope toggle + count chip + Tabs were stock MUI
// (≈48px tabs, default-size toggles) and were the List header's biggest space
// eaters.
import {
  compactToggleXsSx,
  compactTabSx,
  compactTabsSx,
  compactCountBadgeSx,
} from "@aeriosiq/ui";
import { FindingsPanel } from "../../../findings/index.js";
import { DefectsPanel } from "../../../defects/index.js";
import { useImageActivationStore } from "../../state/imageActivationStore.js";
import { useSelectionStore } from "../../state/selectionStore.js";
import type { DefectEditPermission } from "../../editPermission.js";

interface ListWidgetProps {
  inspectionId: string;
  onFindingSelect: (f: AiFinding | null) => void;
  /** Story 008 AC7–AC10 — D-5 role/phase gate for the Defects tab. */
  defectEditPermission?: DefectEditPermission;
  /**
   * SB6-ANA-LIST-02 — id of the DA's active class model (or "none" /
   * "" when no model is selected). Threaded into `<DefectsPanel>` so
   * the new table can hide the Severity column entirely when no class
   * model is active (per OQ-SB6-C5).
   */
  activeClassModelId?: string | null;
  /**
   * SB6-ANA-LIST-01 — DA state, used to pick the default tab on mount:
   *   - `expert_analysis` / `locked` / `signed_off` → default to Defects
   *   - otherwise (pre-screening or anything earlier) → default to AI Findings
   * Christian [01:05:55]: "If a data acquisition has the state expert
   * analysis or if it is locked then by default this defects list
   * should be shown."
   */
  evaluationState?: string | null;
  /**
   * R58-L-07 AC4 — dataset-wide count for the count badge.
   * `<current-image count> / <dataset count>` format. Sourced from
   * the parent's unscoped findings + defects queries (already issued
   * by `AnalysisFeature` for the Filmstrip + overlay). `undefined`
   * while the parent's query is still loading — the badge renders `—`
   * for the dataset denominator in that case.
   */
  datasetFindingsCount?: number | undefined;
  datasetDefectsCount?: number | undefined;
}

type TabKey = "findings" | "defects";
type ListScope = "current" | "all";

const LIST_SCOPE_PARAM = "listScope";
const DEFAULT_SCOPE: ListScope = "current"; // OQ-10 (a)

function readScopeFromUrl(params: URLSearchParams): ListScope {
  const v = params.get(LIST_SCOPE_PARAM);
  if (v === "current" || v === "all") return v;
  return DEFAULT_SCOPE;
}

// SB6-ANA-LIST-01 — DA states where the analyst is reviewing real
// defects, not AI findings. Includes the BE's canonical state-machine
// strings (lowercase and uppercase variants both seen in the wild —
// `evaluationState` reaches this widget straight from the contract).
const DEFECTS_DEFAULT_STATES = new Set<string>([
  "expert_analysis",
  "EXPERT_ANALYSIS",
  "locked",
  "LOCKED",
  "signed_off",
  "SIGNED_OFF",
]);

function pickDefaultTab(evaluationState?: string | null): TabKey {
  if (evaluationState && DEFECTS_DEFAULT_STATES.has(evaluationState)) {
    return "defects";
  }
  return "findings";
}

// SB6-ANA-LIST-03 — `(N)` count for the tab label. Renders `" (12)"` for
// known counts, `" (0)"` when the dataset is loaded with zero items
// (drives the tab's disabled state), or empty when the parent's count
// query hasn't resolved yet (we don't want to render "AI Findings (—)"
// transiently).
//
// SB6-ANA-UI-01 — the count is now a compact 11px tabular-nums span instead
// of inline label text. The TEXT CONTENT is byte-identical to the old
// `${title} (${count})` string so the tab's accessible name is unchanged
// (the e2e specs match `getByRole("tab", { name: "Defects" })` exactly, which
// still holds when the count is undefined → the span isn't rendered).
function renderTabLabel(
  title: string,
  count: number | undefined,
): React.ReactNode {
  if (count === undefined) return title;
  // UI-01 follow-up (2026-06-05): single nowrap line — in a narrow widget the
  // fullWidth Tab used to wrap the count onto a second line, inflating the
  // tab height. Accessible name is unchanged (same text content).
  return (
    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
      {title}{" "}
      <Box component="span" sx={compactCountBadgeSx}>
        ({String(count)})
      </Box>
    </Box>
  );
}

export function ListWidget({
  inspectionId,
  onFindingSelect,
  defectEditPermission,
  activeClassModelId = null,
  evaluationState,
  datasetFindingsCount,
  datasetDefectsCount,
}: ListWidgetProps): React.ReactElement {
  // R58-L-07 AC9 — both tabs now route through imageActivationStore for
  // the Viewer + Tiles + Filmstrip reflow. AI findings used to set the
  // bbox highlight only; defects already pushed both.
  const setActiveImage = useImageActivationStore((s) => s.setActive);
  const setActiveBbox = useSelectionStore((s) => s.setActive);
  const activeBboxId = useSelectionStore((s) => s.activeFindingId);
  const activeBboxKind = useSelectionStore((s) => s.activeKind);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);

  const [active, setActive] = useState<TabKey>(() =>
    pickDefaultTab(evaluationState),
  );

  // R58-L-13 AC7 — reverse-direction effect. When the active selection
  // is published from the Viewer (or any other surface) with a kind
  // hint, switch the visible tab to match (AI -> findings, Defect ->
  // defects) and scroll the matching row into view.
  //
  // BUGFIX (2026-06-05, found in Christian's #185 review): `active` was in
  // the dependency array, so a MANUAL tab click re-ran the effect, which saw
  // the still-active selection and bounced the user straight back — with an
  // AI finding selected, the Defects tab was unreachable (and vice versa).
  // The effect now fires only when the SELECTION changes; `setActive` is
  // idempotent so the previous `active !== …` guards are unnecessary.
  useEffect(() => {
    if (!activeBboxId) return;
    if (activeBboxKind === "defect") {
      setActive("defects");
    } else if (activeBboxKind === "ai") {
      setActive("findings");
    }
    const id = activeBboxId;
    requestAnimationFrame(() => {
      const selector =
        activeBboxKind === "defect"
          ? `[data-defect-id="${id}"]`
          : `[data-finding-id="${id}"]`;
      const row = document.querySelector(selector);
      if (row instanceof HTMLElement) {
        row.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });
  }, [activeBboxId, activeBboxKind]);

  // R58-L-07 AC2 + AC3 — Current / All toggle, persisted in URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = readScopeFromUrl(searchParams);
  const handleScopeChange = (next: ListScope) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === DEFAULT_SCOPE) {
      nextParams.delete(LIST_SCOPE_PARAM);
    } else {
      nextParams.set(LIST_SCOPE_PARAM, next);
    }
    setSearchParams(nextParams, { replace: true });
  };

  // When scope = "current", `imageIdScope` is the active image id (may
  // be null if no image is yet active; in that case the panel falls
  // through to the unscoped query). When scope = "all", the panels see
  // `null` and run their existing unscoped query.
  const imageIdScope =
    scope === "current" && activeImageId ? activeImageId : null;

  // The count badge needs the current-image count from the panel's own
  // query. Each panel exposes its result `total` via the new
  // `onCountChange` callback so we can compose the `<n>/<dataset>`
  // badge in the header without a third parallel query.
  const [currentFindingsCount, setCurrentFindingsCount] = useState<
    number | null
  >(null);
  const [currentDefectsCount, setCurrentDefectsCount] = useState<number | null>(
    null,
  );

  // Reset the per-image count when the scope flips back to "all" — the
  // badge then collapses to the dataset count alone (`/` denominator
  // unchanged).
  useEffect(() => {
    if (scope === "all") {
      setCurrentFindingsCount(null);
      setCurrentDefectsCount(null);
    }
  }, [scope]);

  const findingsCountBadge = renderCountBadge(
    scope === "current" ? currentFindingsCount : datasetFindingsCount,
    datasetFindingsCount,
  );
  const defectsCountBadge = renderCountBadge(
    scope === "current" ? currentDefectsCount : datasetDefectsCount,
    datasetDefectsCount,
  );

  const handleFindingSelect = (f: AiFinding | null) => {
    if (f?.imageId) setActiveImage(f.imageId);
    // R58-L-13 — pass the `'ai'` kind so the reverse-direction effect
    // doesn't switch tabs (we're already on Findings). Without the
    // kind, the effect would treat the click as a "legacy" no-kind
    // selection and stay put — same outcome by accident, but explicit
    // is safer.
    setActiveBbox(f?.id ?? null, f ? "ai" : null);
    onFindingSelect(f);
  };

  return (
    <Stack
      sx={{ height: "100%", overflow: "hidden" }}
      data-testid="list-widget"
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          px: 2,
          py: 1,
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Analysis
        </Typography>
        {/* R58-L-07 AC4 count badge: `<current> / <dataset>`. SB6-ANA-UI-01 —
            11px tabular-nums so the count reads as a dense caption (matches
            the FilterLevel count badges) and digit columns don't jitter. */}
        <Chip
          size="small"
          variant="outlined"
          label={active === "findings" ? findingsCountBadge : defectsCountBadge}
          aria-label={
            active === "findings"
              ? `AI findings count ${findingsCountBadge}`
              : `Defects count ${defectsCountBadge}`
          }
          data-testid="list-widget-count-badge"
          sx={{
            "& .MuiChip-label": {
              fontSize: compactCountBadgeSx.fontSize,
              fontVariantNumeric: compactCountBadgeSx.fontVariantNumeric,
            },
          }}
        />
        {/* R58-L-07 AC2 + AC3 Current / All toggle */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_e, next: ListScope | null) => {
            if (!next) return;
            handleScopeChange(next);
          }}
          aria-label="List scope"
          data-testid="list-widget-scope-toggle"
        >
          {/* UI-01 follow-up — XS (24px chip-height) so the toggles match the
              sync-count Chip beside them instead of towering over it. */}
          <ToggleButton
            value="current"
            aria-label="Current image"
            sx={compactToggleXsSx}
          >
            Current image
          </ToggleButton>
          <ToggleButton
            value="all"
            aria-label="All images"
            sx={compactToggleXsSx}
          >
            All images
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {/* SB6-ANA-UI-01 — dense Tabs (≤36px, 13px labels). Decision 3: keep MUI
          Tabs (they carry the panel-switch semantics) but shed the stock
          ~48px min-height — the single biggest space eater in the List
          header. */}
      <Tabs
        value={active}
        onChange={(_, value: TabKey) => {
          setActive(value);
        }}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          ...compactTabsSx,
        }}
        aria-label="Analysis list tabs"
      >
        {/* SB6-ANA-LIST-03 — tab labels show `(N)` data-point counts.
            Disable the tab when N=0 so the affordance reads as "nothing
            to switch to" instead of an inert button. Christian
            [01:21:18]: "If we show in brackets straight here how many
            AI findings I have in total and how many defects I have in
            total… then if I see brackets zero, so there are no AI
            findings, then it's maybe for me obvious why I cannot press
            this option."
            SB6-ANA-UI-01 — the `(N)` count now renders as an 11px
            tabular-nums badge (compactCountBadgeSx) instead of inline in the
            label string, matching the FilterLevel + count-chip treatment. */}
        <Tab
          value="findings"
          sx={compactTabSx}
          label={renderTabLabel("AI Findings", datasetFindingsCount)}
          disabled={datasetFindingsCount === 0}
        />
        <Tab
          value="defects"
          sx={compactTabSx}
          label={renderTabLabel("Defects", datasetDefectsCount)}
          disabled={datasetDefectsCount === 0}
        />
      </Tabs>
      {/*
        R58-L-07 AC1 — single scrollbar invariant. SB5 Track C.6
        (Q6 locked decision) moves the scroll container ownership down
        into the panel via `VirtualisedRowList`; this outer body runs
        `overflow: hidden` and `display: flex` so the panel's
        virtualised list can grow into the available height with its
        own `overflow: auto`. The end result is still exactly one
        scroll container (the panel's), so AC1 is preserved while AC5
        (row windowing) lands.
      */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        data-testid="list-widget-body"
      >
        <Box
          role="tabpanel"
          hidden={active !== "findings"}
          aria-hidden={active !== "findings"}
          sx={
            active === "findings"
              ? {
                  flex: "1 1 auto",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }
              : {}
          }
        >
          {active === "findings" ? (
            <FindingsPanel
              inspectionId={inspectionId}
              onSelect={handleFindingSelect}
              imageIdScope={imageIdScope}
              scrollFromParent
              onCountChange={setCurrentFindingsCount}
              {...(defectEditPermission
                ? { editPermission: defectEditPermission }
                : {})}
            />
          ) : null}
        </Box>
        <Box
          role="tabpanel"
          hidden={active !== "defects"}
          aria-hidden={active !== "defects"}
          sx={
            active === "defects"
              ? {
                  flex: "1 1 auto",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }
              : {}
          }
        >
          {active === "defects" ? (
            <DefectsPanel
              inspectionId={inspectionId}
              onSelect={(d) => {
                if (d.imageId) setActiveImage(d.imageId);
                // R58-L-13 — publish "defect" kind so the bbox-click
                // reverse-direction effect routes the highlight to the
                // Defects tab when external sources activate this row.
                setActiveBbox(d.id, "defect");
              }}
              imageIdScope={imageIdScope}
              scrollFromParent
              onCountChange={setCurrentDefectsCount}
              activeClassModelId={activeClassModelId}
              {...(defectEditPermission
                ? { editPermission: defectEditPermission }
                : {})}
            />
          ) : null}
        </Box>
      </Box>
    </Stack>
  );
}

function renderCountBadge(
  current: number | null | undefined,
  dataset: number | undefined,
): string {
  // The dataset count may be `undefined` while the parent's unscoped
  // query is still loading. Render `—` so the badge slot keeps its
  // width and doesn't pop in.
  const datasetStr = dataset == null ? "—" : String(dataset);
  if (current == null) return datasetStr;
  return `${String(current)} / ${datasetStr}`;
}
